<template>
  <div class="border-gray-200 dark:border-gray-700 pt-4">
    <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
      {{ tt('Device Values', 'Vrednosti naprave') }}
    </h4>
    <div v-if="liveValues.length > 0" class="space-y-2">
      <div
        v-for="val in liveValues"
        :key="val.valueId || val.valueKey"
        class="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
        <div class="flex items-start justify-between">
          <div>
            <div class="text-sm font-medium text-gray-900 dark:text-white">
              {{ val.name }}
              <span v-if="val.grouping" class="ml-2 text-xs text-gray-500 dark:text-gray-400"
                >({{ val.grouping }})</span
              >
              <span :class="['ml-2 px-1.5 py-0.5 text-xs rounded font-medium', getValueTypeStyle(val.valueType)]">
                {{ val.valueType }}
              </span>
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ tt('Key:', 'Ključ:') }} {{ val.valueKey }}</div>
            <div class="mt-1 text-xs text-gray-700 dark:text-gray-300">
              {{ tt('Current:', 'Trenutno:') }}
              <span class="font-semibold text-gray-900 dark:text-white">{{
                formatSensorValue(val.currentValue, val.valueType, val.unit, val.decimalPlaces)
              }}</span>
            </div>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <span
              v-if="val.modeSettable"
              class="px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
              >{{ tt('settable', 'nastavljivo') }}</span
            >
            <span v-else class="px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">{{
              tt('read-only', 'samo za branje')
            }}</span>
            <select
              v-if="val.modeSettable && deviceId && val.source === 'user'"
              :value="modeCategoryDrafts[valueIdOf(val)] ?? val.modeCategory ?? ''"
              @change="saveModeCategory(val, ($event.target as HTMLSelectElement).value)"
              :disabled="savingModeCategory === valueIdOf(val)"
              class="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs"
              :title="
                tt(
                  'Mode category for this custom value (scene, security, climate, season, lighting, shading)',
                  'Kategorija načina za to vrednost po meri (scena, varnost, klima, letni čas, razsvetljava)',
                )
              ">
              <option value="">{{ tt('— category —', '— kategorija —') }}</option>
              <option v-for="c in MODE_CATEGORIES" :key="c.value" :value="c.value">{{ tt(...c.label) }}</option>
            </select>
            <span
              v-else-if="deviceId"
              class="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs"
              :title="
                tt(
                  'Mode category is fixed by the device / automation type',
                  'Kategorijo načina določa vrsta naprave / avtomatizacije',
                )
              ">
              {{ categoryLabel(val.modeCategory) }}
            </span>
          </div>
        </div>
        <!-- Numeric alert values expose when their alert fires: the level the
             value has to reach, instead of "any change from zero". -->
        <div
          v-if="supportsThreshold(val)"
          class="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <label class="font-medium">{{ tt('Alert threshold', 'Prag opozorila') }}</label>
          <input
            type="number"
            step="any"
            :value="thresholdDrafts[valueIdOf(val)] ?? val.notificationThreshold ?? ''"
            @change="saveNotificationThreshold(val, ($event.target as HTMLInputElement).value)"
            :disabled="savingNotificationThreshold === valueIdOf(val)"
            class="w-24 px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs disabled:opacity-50"
            :title="
              tt(
                'Alerts when the value reaches this level; leave empty to alert on any change from zero',
                'Opozorilo, ko vrednost doseže to raven; prazno pomeni opozorilo ob vsaki spremembi z ničle',
              )
            " />
          <span v-if="val.unit">{{ val.unit }}</span>
        </div>
        <div class="mt-3 space-y-3">
          <!-- WLED values are auto-linked by the driver to the board segment -->
          <p v-if="isWLED" class="text-xs text-gray-500 dark:text-gray-400 italic">
            {{
              tt(
                'Auto-linked to the WLED segment — managed by the integration.',
                'Samodejno povezano s segmentom WLED — upravlja integracija.',
              )
            }}
          </p>
          <!-- Multiple References (inline list for values with multiple mappings) -->
          <div v-if="getAllMappings(val.valueKey).length > 0">
            <div class="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">
              {{ tt('References', 'Reference') }}
            </div>
            <!-- Grid layout for side-by-side display -->
            <div class="grid grid-cols-2 gap-4">
              <div
                v-for="(mapping, mappingIndex) in getAllMappings(val.valueKey)"
                :key="`${val.valueKey}-${mapping.referenceType}-${mappingIndex}`"
                class="flex flex-col">
                <!-- Reference Label as header -->
                <div class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {{ formatMappingLabel(mapping) }}
                </div>

                <!-- Reference Input -->
                <div v-if="isKNX" class="flex-1">
                  <KNXReferenceInput
                    :model-value="getConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateConfigForMapping(val.valueKey, mapping, $event)"
                    :mapping="mapping"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isMatter" class="flex-1">
                  <MatterReferenceInput
                    :model-value="getMatterConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateMatterConfigForMapping(val.valueKey, mapping, $event)"
                    :mapping="mapping"
                    :integration-id="integrationId"
                    :matter-endpoint-id="matterEndpointId" />
                </div>
                <div v-else-if="isModbus" class="flex-1">
                  <ModbusReferenceInput
                    :model-value="getModbusConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateModbusConfigForMapping(val.valueKey, mapping, $event)"
                    :mapping="mapping"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isMQTT" class="flex-1">
                  <MQTTReferenceInput
                    :model-value="getMqttConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateMqttConfigForMapping(val.valueKey, mapping, $event)"
                    :mapping="mapping"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isBacnet" class="flex-1">
                  <BacnetReferenceInput
                    :model-value="getBacnetConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateBacnetConfigForMapping(val.valueKey, mapping, $event)"
                    :mapping="mapping"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isESPHome" class="flex-1">
                  <ESPHomeReferenceInput
                    :model-value="getESPHomeConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateESPHomeConfigForMapping(val.valueKey, mapping, $event)"
                    :integration-id="integrationId"
                    :reference-type="mapping.referenceType === 'control' ? 'control' : 'status'" />
                </div>
                <div v-else-if="isZWave" class="flex-1">
                  <ZWaveReferenceInput
                    :model-value="getZWaveConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateZWaveConfigForMapping(val.valueKey, mapping, $event)"
                    :integration-id="integrationId"
                    :reference-type="mapping.referenceType === 'control' ? 'control' : 'status'" />
                </div>
                <div v-else-if="isZigbee" class="flex-1">
                  <ZigbeeReferenceInput
                    :model-value="getZigbeeConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateZigbeeConfigForMapping(val.valueKey, mapping, $event)"
                    :integration-id="integrationId"
                    :reference-type="mapping.referenceType === 'control' ? 'control' : 'status'" />
                </div>
                <div v-else-if="isHA" class="flex-1">
                  <HAEntityReferenceInput
                    :model-value="getHAConfigForMapping(val.valueKey, mapping)"
                    @update:model-value="updateHAConfigForMapping(val.valueKey, mapping, $event)"
                    :integration-id="integrationId"
                    :reference-type="mapping.referenceType === 'control' ? 'control' : 'status'"
                    :value-type="val.valueType" />
                </div>
                <div v-else class="flex-1 text-xs text-gray-500 dark:text-gray-400 italic">
                  {{ tt('(reference)', '(referenca)') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Fallback: Show references without mappings (for older devices or devices being edited) -->
          <div v-else-if="shouldShowFallbackReferences(val)">
            <div class="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">
              {{ tt('References', 'Reference') }}
            </div>
            <div class="grid grid-cols-2 gap-4">
              <!-- Control Reference -->
              <div v-if="shouldShowControlReference(val)" class="flex flex-col">
                <div class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {{ tt('Control', 'Krmiljenje') }}
                </div>
                <div v-if="isKNX" class="flex-1">
                  <KNXReferenceInput
                    :model-value="controlKnx[val.valueKey]"
                    @update:model-value="updateControlKnx(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isMatter" class="flex-1">
                  <MatterReferenceInput
                    :model-value="controlMatter?.[val.valueKey]"
                    @update:model-value="updateControlMatter(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId"
                    :matter-endpoint-id="matterEndpointId" />
                </div>
                <div v-else-if="isModbus" class="flex-1">
                  <ModbusReferenceInput
                    :model-value="controlModbus?.[val.valueKey]"
                    @update:model-value="updateControlModbus(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isMQTT" class="flex-1">
                  <MQTTReferenceInput
                    :model-value="controlMqtt?.[val.valueKey]"
                    @update:model-value="updateControlMqtt(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isBacnet" class="flex-1">
                  <BacnetReferenceInput
                    :model-value="controlBacnet?.[val.valueKey]"
                    @update:model-value="updateControlBacnet(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isESPHome" class="flex-1">
                  <ESPHomeReferenceInput
                    :model-value="controlEsphome?.[val.valueKey]"
                    @update:model-value="updateControlEsphome(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="control" />
                </div>
                <div v-else-if="isZWave" class="flex-1">
                  <ZWaveReferenceInput
                    :model-value="controlZwave?.[val.valueKey]"
                    @update:model-value="updateControlZwave(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="control" />
                </div>
                <div v-else-if="isZigbee" class="flex-1">
                  <ZigbeeReferenceInput
                    :model-value="controlZigbee?.[val.valueKey]"
                    @update:model-value="updateControlZigbee(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="control" />
                </div>
                <div v-else-if="isHA" class="flex-1">
                  <HAEntityReferenceInput
                    :model-value="controlHa?.[val.valueKey]"
                    @update:model-value="updateControlHa(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="control"
                    :value-type="val.valueType" />
                </div>
              </div>

              <!-- Status Reference -->
              <div v-if="shouldShowStatusReference(val)" class="flex flex-col">
                <div class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {{ tt('Status', 'Stanje') }}
                </div>
                <div v-if="isKNX" class="flex-1">
                  <KNXReferenceInput
                    :model-value="statusKnx[val.valueKey]"
                    @update:model-value="updateStatusKnx(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isMatter" class="flex-1">
                  <MatterReferenceInput
                    :model-value="statusMatter?.[val.valueKey]"
                    @update:model-value="updateStatusMatter(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId"
                    :matter-endpoint-id="matterEndpointId" />
                </div>
                <div v-else-if="isModbus" class="flex-1">
                  <ModbusReferenceInput
                    :model-value="statusModbus?.[val.valueKey]"
                    @update:model-value="updateStatusModbus(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isMQTT" class="flex-1">
                  <MQTTReferenceInput
                    :model-value="statusMqtt?.[val.valueKey]"
                    @update:model-value="updateStatusMqtt(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isBacnet" class="flex-1">
                  <BacnetReferenceInput
                    :model-value="statusBacnet?.[val.valueKey]"
                    @update:model-value="updateStatusBacnet(val.valueKey, $event)"
                    :mapping="undefined"
                    :integration-id="integrationId" />
                </div>
                <div v-else-if="isESPHome" class="flex-1">
                  <ESPHomeReferenceInput
                    :model-value="statusEsphome?.[val.valueKey]"
                    @update:model-value="updateStatusEsphome(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="status" />
                </div>
                <div v-else-if="isZWave" class="flex-1">
                  <ZWaveReferenceInput
                    :model-value="statusZwave?.[val.valueKey]"
                    @update:model-value="updateStatusZwave(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="status" />
                </div>
                <div v-else-if="isZigbee" class="flex-1">
                  <ZigbeeReferenceInput
                    :model-value="statusZigbee?.[val.valueKey]"
                    @update:model-value="updateStatusZigbee(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="status" />
                </div>
                <div v-else-if="isHA" class="flex-1">
                  <HAEntityReferenceInput
                    :model-value="statusHa?.[val.valueKey]"
                    @update:model-value="updateStatusHa(val.valueKey, $event)"
                    :integration-id="integrationId"
                    reference-type="status"
                    :value-type="val.valueType" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-xs text-gray-500 dark:text-gray-400 italic">
      {{ tt('No values found for this device.', 'Za to napravo ni najdenih vrednosti.') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref, onMounted } from 'vue';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';
import { useDeviceValues } from '../composables/useDeviceValues';
import KNXReferenceInput from './KNXReferenceInput.vue';
import MatterReferenceInput from './MatterReferenceInput.vue';
import BacnetReferenceInput, { type BacnetConfig } from './BacnetReferenceInput.vue';
import ModbusReferenceInput, { type ModbusConfig } from './ModbusReferenceInput.vue';
import MQTTReferenceInput, { type MQTTConfig } from './MQTTReferenceInput.vue';
import ESPHomeReferenceInput, { type ESPHomeEntityRef } from './ESPHomeReferenceInput.vue';
import ZWaveReferenceInput, { type ZWaveValueRef } from './ZWaveReferenceInput.vue';
import ZigbeeReferenceInput, { type ZigbeeAttributeRef } from './ZigbeeReferenceInput.vue';
import HAEntityReferenceInput, { type HAEntityRef } from './HAEntityReferenceInput.vue';
import type { PropertyMapping } from '../shared/propertyMapping';

interface DeviceValueReference {
  reference_id?: string;
  valueId?: string;
  propertyType: string;
  propertyId: string;
  createdAt?: Date | string;
}

interface DeviceValue {
  valueId?: string;
  id?: string;
  deviceId?: string;
  valueKey: string;
  name: string;
  valueType: string;
  grouping?: string;
  modeSettable?: boolean;
  modeCategory?: string | null;
  source?: string;
  unit?: string;
  decimalPlaces?: number;
  notificationLevel?: string | null;
  notificationThreshold?: number | null;
  currentValue?: unknown;
  controlReference?: DeviceValueReference;
  statusReference?: DeviceValueReference;
}

interface KNXConfig {
  groupAddress: string;
  dpt: number;
}

interface MatterConfig {
  propertyId: string;
  clusterId: number;
  attributeId: number;
}

type ESPHomeConfig = ESPHomeEntityRef;

type ZWaveConfig = ZWaveValueRef;
type ZigbeeConfig = ZigbeeAttributeRef;
type HAConfig = HAEntityRef;

interface Props {
  deviceValues: DeviceValue[];
  deviceId?: string;
  controlKnx: Record<string, KNXConfig>;
  statusKnx: Record<string, KNXConfig>;
  controlMatter?: Record<string, MatterConfig>;
  statusMatter?: Record<string, MatterConfig>;
  controlBacnet?: Record<string, BacnetConfig>;
  statusBacnet?: Record<string, BacnetConfig>;
  controlModbus?: Record<string, ModbusConfig>;
  statusModbus?: Record<string, ModbusConfig>;
  controlMqtt?: Record<string, MQTTConfig>;
  statusMqtt?: Record<string, MQTTConfig>;
  controlEsphome?: Record<string, ESPHomeConfig>;
  statusEsphome?: Record<string, ESPHomeConfig>;
  controlZwave?: Record<string, ZWaveConfig>;
  statusZwave?: Record<string, ZWaveConfig>;
  controlZigbee?: Record<string, ZigbeeConfig>;
  statusZigbee?: Record<string, ZigbeeConfig>;
  controlHa?: Record<string, HAConfig>;
  statusHa?: Record<string, HAConfig>;
  integrationId: string;
  integrationType: string;
  integrationMappings?: PropertyMapping[];
  /** UUID of device_endpoints.protocol_endpoint_id — scopes Matter picker to this endpoint */
  matterEndpointId?: string;
}

const props = defineProps<Props>();

/**
 * The value mode categories, in the same reading order as the automation
 * categories: Security, Lights, Shades, Climate, Water first, then the two that
 * only a *value* can carry (Scene, Season) and None.
 */
const MODE_CATEGORIES: { value: string; label: [string, string] }[] = [
  { value: 'security', label: ['Security', 'Varnost'] },
  { value: 'lighting', label: ['Lighting', 'Razsvetljava'] },
  { value: 'shading', label: ['Shading', 'Senčenje'] },
  { value: 'climate', label: ['Climate', 'Klima'] },
  { value: 'water', label: ['Water', 'Voda'] },
  { value: 'scene', label: ['Scene', 'Scena'] },
  { value: 'season', label: ['Season', 'Letni čas'] },
  { value: 'none', label: ['None', 'Brez'] },
];

const modeCategoryDrafts = ref<Record<string, string>>({});
const savingModeCategory = ref<string | null>(null);
const thresholdDrafts = ref<Record<string, string>>({});
const savingNotificationThreshold = ref<string | null>(null);

function categoryLabel(category?: string | null): string {
  const found = MODE_CATEGORIES.find((item) => item.value === String(category ?? '').toLowerCase());
  return found ? tt(...found.label) : '—';
}

/** The id keys a device-value row may carry (the hub spells it per source). */
interface ValueIdCarrier {
  valueId?: string;
  id?: string;
  valueKey?: string;
  modeCategory?: string | null;
}

function valueIdOf(val: ValueIdCarrier): string {
  return val.valueId || val.id || val.valueKey || '';
}

async function saveModeCategory(val: ValueIdCarrier, category: string) {
  const valueId = val.valueId || val.id;
  if (!valueId || !props.deviceId) return;
  const key = valueIdOf(val);
  modeCategoryDrafts.value = { ...modeCategoryDrafts.value, [key]: category };
  savingModeCategory.value = key;
  try {
    await apiFetch(`/devices/${props.deviceId}/values/${valueId}`, {
      method: 'PATCH',
      body: JSON.stringify({ modeCategory: category || null }),
    });
    if (val.modeCategory !== undefined) val.modeCategory = category || null;
  } catch (err) {
    console.error('[DeviceValuesEditor] Failed to update mode category', err);
  } finally {
    savingModeCategory.value = null;
  }
}

/** Numeric alert values are the ones a threshold means anything to. */
function supportsThreshold(val: DeviceValue): boolean {
  return (
    val.valueType === 'number' &&
    !!val.notificationLevel &&
    val.notificationLevel !== 'none' &&
    !!(val.valueId || val.id) &&
    !!deviceId.value
  );
}

async function saveNotificationThreshold(val: DeviceValue, raw: string) {
  const valueId = val.valueId || val.id;
  if (!valueId || !props.deviceId) return;
  const key = valueIdOf(val);
  const trimmed = raw.trim();
  const next = trimmed === '' ? null : Number(trimmed);
  if (next !== null && !Number.isFinite(next)) return;
  thresholdDrafts.value = { ...thresholdDrafts.value, [key]: trimmed };
  savingNotificationThreshold.value = key;
  try {
    await apiFetch(`/devices/${props.deviceId}/values/${valueId}`, {
      method: 'PATCH',
      body: JSON.stringify({ notificationThreshold: next }),
    });
    val.notificationThreshold = next;
  } catch (err) {
    console.error('[DeviceValuesEditor] Failed to update notification threshold', err);
  } finally {
    // Drop the draft either way: on success the stored value is the new one,
    // on failure the input falls back to what the hub still holds.
    delete thresholdDrafts.value[key];
    savingNotificationThreshold.value = null;
  }
}

const emit = defineEmits<{
  'update:controlKnx': [value: Record<string, KNXConfig>];
  'update:statusKnx': [value: Record<string, KNXConfig>];
  'update:controlMatter': [value: Record<string, MatterConfig>];
  'update:statusMatter': [value: Record<string, MatterConfig>];
  'update:controlBacnet': [value: Record<string, BacnetConfig>];
  'update:statusBacnet': [value: Record<string, BacnetConfig>];
  'update:controlModbus': [value: Record<string, ModbusConfig>];
  'update:statusModbus': [value: Record<string, ModbusConfig>];
  'update:controlMqtt': [value: Record<string, MQTTConfig>];
  'update:statusMqtt': [value: Record<string, MQTTConfig>];
  'update:controlEsphome': [value: Record<string, ESPHomeConfig>];
  'update:statusEsphome': [value: Record<string, ESPHomeConfig>];
  'update:controlZwave': [value: Record<string, ZWaveConfig>];
  'update:statusZwave': [value: Record<string, ZWaveConfig>];
  'update:controlZigbee': [value: Record<string, ZigbeeConfig>];
  'update:statusZigbee': [value: Record<string, ZigbeeConfig>];
  'update:controlHa': [value: Record<string, HAConfig>];
  'update:statusHa': [value: Record<string, HAConfig>];
}>();

// Create a reactive copy of device values that can be updated in real-time
const liveValues = ref<DeviceValue[]>([...props.deviceValues]);

// Initialize liveValues when props change
watch(
  () => props.deviceValues,
  (newValues) => {
    liveValues.value = [...newValues];
  },
  { immediate: true, deep: true },
);

// Subscribe to real-time device value updates if we have a deviceId
const deviceId = computed(() => props.deviceValues[0]?.deviceId);

// Only set up subscription if we have a deviceId
let deviceValuesComposable: ReturnType<typeof useDeviceValues> | null = null;

onMounted(() => {
  if (deviceId.value) {
    console.log('[DeviceValuesEditor] Setting up real-time subscription for device:', deviceId.value);
    deviceValuesComposable = useDeviceValues(deviceId.value);

    // Watch for changes in currentValues and update liveValues
    watch(
      () => deviceValuesComposable?.currentValues.value,
      (currentValues) => {
        if (!currentValues) return;

        // Update matching values in liveValues
        Object.keys(currentValues).forEach((valueKey) => {
          const valueIndex = liveValues.value.findIndex((v) => v.valueKey === valueKey);
          if (valueIndex !== -1) {
            console.log('[DeviceValuesEditor] Updating value:', valueKey, '=', currentValues[valueKey]);
            liveValues.value[valueIndex].currentValue = currentValues[valueKey];
          }
        });
      },
      { deep: true },
    );
  }
});

// Debug: Watch deviceValues to see what data we're receiving
watch(
  () => props.deviceValues,
  (newValues) => {
    console.log('[DeviceValuesEditor] deviceValues updated:', newValues);
    if (newValues.length > 0) {
      console.log('[DeviceValuesEditor] First value sample:', {
        valueKey: newValues[0].valueKey,
        name: newValues[0].name,
        currentValue: newValues[0].currentValue,
        current_value_type: typeof newValues[0].currentValue,
      });
    }
  },
  { immediate: true, deep: true },
);

const isKNX = computed(() => {
  const result = props.integrationType.toLowerCase().includes('knx');
  console.log('[DeviceValuesEditor] isKNX:', result, 'integrationType:', props.integrationType);
  return result;
});
const isMatter = computed(() => {
  const result = props.integrationType.toLowerCase().includes('matter');
  console.log('[DeviceValuesEditor] isMatter:', result, 'integrationType:', props.integrationType);
  return result;
});
const isModbus = computed(() => {
  const result = props.integrationType.toLowerCase().includes('modbus');
  console.log('[DeviceValuesEditor] isModbus:', result, 'integrationType:', props.integrationType);
  return result;
});
const isMQTT = computed(() => {
  const result = props.integrationType.toLowerCase().includes('mqtt');
  console.log('[DeviceValuesEditor] isMQTT:', result, 'integrationType:', props.integrationType);
  return result;
});
const isBacnet = computed(() => {
  const result = props.integrationType.toLowerCase().includes('bacnet');
  console.log('[DeviceValuesEditor] isBacnet:', result, 'integrationType:', props.integrationType);
  return result;
});
const isESPHome = computed(() => {
  const result = props.integrationType.toLowerCase().includes('esphome');
  console.log('[DeviceValuesEditor] isESPHome:', result, 'integrationType:', props.integrationType);
  return result;
});
const isZWave = computed(() => {
  const result = props.integrationType.toLowerCase().includes('zwave');
  console.log('[DeviceValuesEditor] isZWave:', result, 'integrationType:', props.integrationType);
  return result;
});
const isZigbee = computed(() => {
  const result = props.integrationType.toLowerCase().includes('zigbee');
  console.log('[DeviceValuesEditor] isZigbee:', result, 'integrationType:', props.integrationType);
  return result;
});
const isHA = computed(() => {
  const result = props.integrationType.toLowerCase().includes('homeassistant');
  console.log('[DeviceValuesEditor] isHA:', result, 'integrationType:', props.integrationType);
  return result;
});

// WLED value references are created by the driver (autoCreateDevices) and
// managed by the integration — there is no manual per-value linking.
const isWLED = computed(() => props.integrationType.toLowerCase().includes('wled'));

// Debug: Watch all Matter props to see what's being passed
watch(
  () => ({
    controlMatter: props.controlMatter,
    statusMatter: props.statusMatter,
    mappings: props.integrationMappings,
  }),
  (newVal) => {
    console.log('[DeviceValuesEditor] Matter props updated:', {
      controlMatter: newVal.controlMatter,
      statusMatter: newVal.statusMatter,
      mappings: newVal.mappings,
      mappingsCount: newVal.mappings?.length || 0,
      mappingsDetails: JSON.stringify(newVal.mappings, null, 2),
    });

    // Check what getAllMappings returns
    if (props.deviceValues.length > 0) {
      const firstValueKey = props.deviceValues[0].valueKey;
      const mappingsForFirst = getAllMappings(firstValueKey);
      console.log(`[DeviceValuesEditor] getAllMappings('${firstValueKey}') returns:`, mappingsForFirst);
    }
  },
  { immediate: true, deep: true },
);

// Get all mappings for a value (supports multiple mappings per value)
function getAllMappings(valueKey: string): PropertyMapping[] {
  if (!props.integrationMappings) {
    console.log('[DeviceValuesEditor] getAllMappings: no integrationMappings');
    return [];
  }
  const result = props.integrationMappings.filter((m) => m.valueKey === valueKey);
  console.log(`[DeviceValuesEditor] getAllMappings('${valueKey}'):`, result);
  return result;
}

// Check if a value has any references (control or status) - fallback for devices without mappings
function hasAnyReferences(valueKey: string): boolean {
  return hasControlReference(valueKey) || hasStatusReference(valueKey);
}

// True when the class has no protocol-scoped mappings for this value on the
// device's integration — the value is then linked fully manually.
function hasNoMappingsForValue(valueKey: string): boolean {
  return getAllMappings(valueKey).length === 0;
}

function shouldShowFallbackReferences(value: DeviceValue): boolean {
  if (isWLED.value) return false;
  if (isESPHome.value || hasNoMappingsForValue(value.valueKey)) {
    return true;
  }
  return hasAnyReferences(value.valueKey);
}

function shouldShowControlReference(value: DeviceValue): boolean {
  if (isWLED.value) return false;
  if (hasNoMappingsForValue(value.valueKey)) {
    // Manual linking: always offer control for settable values.
    return Boolean(value.modeSettable);
  }
  if (isESPHome.value) {
    return Boolean(value.modeSettable) || hasControlReference(value.valueKey);
  }
  return hasControlReference(value.valueKey);
}

function shouldShowStatusReference(value: DeviceValue): boolean {
  if (isWLED.value) return false;
  if (hasNoMappingsForValue(value.valueKey) || isESPHome.value) {
    return true;
  }
  return hasStatusReference(value.valueKey);
}

// Check if a value has a control reference set
function hasControlReference(valueKey: string): boolean {
  if (isKNX.value) {
    const control = props.controlKnx[valueKey];
    return !!(control && control.groupAddress && control.groupAddress.trim());
  } else if (isMatter.value) {
    const control = props.controlMatter?.[valueKey];
    return !!(control && control.propertyId && control.propertyId.trim());
  } else if (isModbus.value) {
    const control = props.controlModbus?.[valueKey];
    return !!(control && control.propertyId && control.propertyId.trim());
  } else if (isMQTT.value) {
    const control = props.controlMqtt?.[valueKey];
    return !!(control && control.propertyId && control.propertyId.trim());
  } else if (isBacnet.value) {
    const control = props.controlBacnet?.[valueKey];
    return !!(control && control.propertyId && control.propertyId.trim());
  } else if (isESPHome.value) {
    const control = props.controlEsphome?.[valueKey];
    return !!(control && control.esphomeEntityId && control.esphomeEntityId.trim());
  } else if (isZWave.value) {
    const control = props.controlZwave?.[valueKey];
    return !!(control && control.zwaveValueId && control.zwaveValueId.trim());
  } else if (isZigbee.value) {
    const control = props.controlZigbee?.[valueKey];
    return !!(control && control.zigbeeAttributeId && control.zigbeeAttributeId.trim());
  } else if (isHA.value) {
    const control = props.controlHa?.[valueKey];
    return !!(control && control.haEntityId && control.haEntityId.trim());
  }
  return false;
}

// Check if a value has a status reference set
function hasStatusReference(valueKey: string): boolean {
  if (isKNX.value) {
    const status = props.statusKnx[valueKey];
    return !!(status && status.groupAddress && status.groupAddress.trim());
  } else if (isMatter.value) {
    const status = props.statusMatter?.[valueKey];
    return !!(status && status.propertyId && status.propertyId.trim());
  } else if (isModbus.value) {
    const status = props.statusModbus?.[valueKey];
    return !!(status && status.propertyId && status.propertyId.trim());
  } else if (isMQTT.value) {
    const status = props.statusMqtt?.[valueKey];
    return !!(status && status.propertyId && status.propertyId.trim());
  } else if (isBacnet.value) {
    const status = props.statusBacnet?.[valueKey];
    return !!(status && status.propertyId && status.propertyId.trim());
  } else if (isESPHome.value) {
    const status = props.statusEsphome?.[valueKey];
    return !!(status && status.esphomeEntityId && status.esphomeEntityId.trim());
  } else if (isZWave.value) {
    const status = props.statusZwave?.[valueKey];
    return !!(status && status.zwaveValueId && status.zwaveValueId.trim());
  } else if (isZigbee.value) {
    const status = props.statusZigbee?.[valueKey];
    return !!(status && status.zigbeeAttributeId && status.zigbeeAttributeId.trim());
  } else if (isHA.value) {
    const status = props.statusHa?.[valueKey];
    return !!(status && status.haEntityId && status.haEntityId.trim());
  }
  return false;
}

// Update control KNX config (simple key, for fallback)
function updateControlKnx(valueKey: string, config: KNXConfig) {
  const updated = { ...props.controlKnx, [valueKey]: config };
  emit('update:controlKnx', updated);
}

// Update status KNX config (simple key, for fallback)
function updateStatusKnx(valueKey: string, config: KNXConfig) {
  const updated = { ...props.statusKnx, [valueKey]: config };
  emit('update:statusKnx', updated);
}

// Update control Matter config (simple key, for fallback)
function updateControlMatter(valueKey: string, config: MatterConfig) {
  const updated = { ...(props.controlMatter || {}), [valueKey]: config };
  emit('update:controlMatter', updated);
}

// Update status Matter config (simple key, for fallback)
function updateStatusMatter(valueKey: string, config: MatterConfig) {
  const updated = { ...(props.statusMatter || {}), [valueKey]: config };
  emit('update:statusMatter', updated);
}

function updateControlBacnet(valueKey: string, config: BacnetConfig) {
  const updated = { ...(props.controlBacnet || {}), [valueKey]: config };
  emit('update:controlBacnet', updated);
}

function updateStatusBacnet(valueKey: string, config: BacnetConfig) {
  const updated = { ...(props.statusBacnet || {}), [valueKey]: config };
  emit('update:statusBacnet', updated);
}

function updateControlModbus(valueKey: string, config: ModbusConfig) {
  const updated = { ...(props.controlModbus || {}), [valueKey]: config };
  emit('update:controlModbus', updated);
}

function updateStatusModbus(valueKey: string, config: ModbusConfig) {
  const updated = { ...(props.statusModbus || {}), [valueKey]: config };
  emit('update:statusModbus', updated);
}

function updateControlMqtt(valueKey: string, config: MQTTConfig) {
  const updated = { ...(props.controlMqtt || {}), [valueKey]: config };
  emit('update:controlMqtt', updated);
}

function updateStatusMqtt(valueKey: string, config: MQTTConfig) {
  const updated = { ...(props.statusMqtt || {}), [valueKey]: config };
  emit('update:statusMqtt', updated);
}

function updateControlEsphome(valueKey: string, config: ESPHomeConfig | undefined) {
  const updated = { ...(props.controlEsphome || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:controlEsphome', updated);
}

function updateStatusEsphome(valueKey: string, config: ESPHomeConfig | undefined) {
  const updated = { ...(props.statusEsphome || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:statusEsphome', updated);
}

function updateControlZwave(valueKey: string, config: ZWaveConfig | undefined) {
  const updated = { ...(props.controlZwave || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:controlZwave', updated);
}

function updateStatusZwave(valueKey: string, config: ZWaveConfig | undefined) {
  const updated = { ...(props.statusZwave || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:statusZwave', updated);
}

function updateControlZigbee(valueKey: string, config: ZigbeeConfig | undefined) {
  const updated = { ...(props.controlZigbee || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:controlZigbee', updated);
}

function updateStatusZigbee(valueKey: string, config: ZigbeeConfig | undefined) {
  const updated = { ...(props.statusZigbee || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:statusZigbee', updated);
}

function updateControlHa(valueKey: string, config: HAConfig | undefined) {
  const updated = { ...(props.controlHa || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:controlHa', updated);
}

function updateStatusHa(valueKey: string, config: HAConfig | undefined) {
  const updated = { ...(props.statusHa || {}) };
  if (config) {
    updated[valueKey] = config;
  } else {
    delete updated[valueKey];
  }
  emit('update:statusHa', updated);
}

// Format a mapping label (e.g., "Red Control", "Green Status", "Hue")
function formatMappingLabel(mapping: PropertyMapping): string {
  console.log('[DeviceValuesEditor] formatMappingLabel called with:', mapping);
  let label = '';

  // Use matterRole for Matter devices, knxRole for KNX devices
  if (mapping.matterRole) {
    const role = mapping.matterRole.replace(/_/g, ' ');
    // Capitalize first letter of each word
    label = role
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } else if (mapping.knxRole) {
    const role = mapping.knxRole.replace(/_status$/, '').replace(/_control$/, '');
    // Capitalize first letter
    label = role.charAt(0).toUpperCase() + role.slice(1);
  } else {
    label = mapping.valueKey;
  }

  // Add reference type suffix only if there are multiple mappings for this value AND not attribute type
  const mappingsForValue = getAllMappings(mapping.valueKey);
  if (mappingsForValue.length > 1 && mapping.referenceType !== 'attribute') {
    label += mapping.referenceType === 'control' ? tt(' Control', ' Krmiljenje') : tt(' Status', ' Stanje');
  }

  console.log('[DeviceValuesEditor] formatMappingLabel result:', label);
  return label;
}

// Create a unique key for a mapping config
function getMappingKey(valueKey: string, mapping: PropertyMapping, index: number): string {
  // Use index to create unique key for each mapping
  return `${valueKey}__${index}`;
}

// Get config for a specific mapping
function getConfigForMapping(valueKey: string, mapping: PropertyMapping): KNXConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);

  // First try the composite key (for new multi-channel devices)
  if (mapping.referenceType === 'control' && props.controlKnx[key]) {
    return props.controlKnx[key];
  }
  if (mapping.referenceType === 'status' && props.statusKnx[key]) {
    return props.statusKnx[key];
  }

  // ALWAYS try simple valueKey as fallback - existing devices from API use simple keys
  if (mapping.referenceType === 'control') {
    return props.controlKnx[valueKey];
  }
  return props.statusKnx[valueKey];
}

// Update config for a specific mapping
function updateConfigForMapping(valueKey: string, mapping: PropertyMapping, config: KNXConfig) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);

  if (mapping.referenceType === 'control') {
    const updated = { ...props.controlKnx, [key]: config };
    emit('update:controlKnx', updated);
  } else {
    const updated = { ...props.statusKnx, [key]: config };
    emit('update:statusKnx', updated);
  }
}

// Get Matter config for a specific mapping
function getMatterConfigForMapping(valueKey: string, mapping: PropertyMapping): MatterConfig {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  console.log(`[DeviceValuesEditor] getMatterConfigForMapping('${valueKey}'):`, {
    mapping,
    mappingIndex,
    allMappingsCount: allMappings.length,
    controlMatter: props.controlMatter,
    statusMatter: props.statusMatter,
  });

  if (mappingIndex === -1) return { propertyId: '', clusterId: 0, attributeId: 0 };

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  // For 'attribute' type, use controlMatter; otherwise use the matching type
  const source =
    mapping.referenceType === 'control' || mapping.referenceType === 'attribute'
      ? props.controlMatter
      : props.statusMatter;

  console.log(`[DeviceValuesEditor] Looking up key '${key}' in ${mapping.referenceType}:`, source?.[key]);

  // Try composite key first (for new multi-channel devices)
  if (source?.[key]) {
    return source[key];
  }

  // ALWAYS try simple valueKey as fallback - existing devices from API use simple keys
  // Even when there are multiple mappings (control + status), the API returns them
  // as controlMatter[valueKey] and statusMatter[valueKey], not composite keys
  const result = source?.[valueKey] || { propertyId: '', clusterId: 0, attributeId: 0 };
  console.log(`[DeviceValuesEditor] Using simple valueKey '${valueKey}':`, result);
  return result;
}

// Update Matter config for a specific mapping
function updateMatterConfigForMapping(valueKey: string, mapping: PropertyMapping, config: MatterConfig) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);

  // For 'attribute' type, use controlMatter; otherwise use the matching type
  if (mapping.referenceType === 'control' || mapping.referenceType === 'attribute') {
    const updated = { ...(props.controlMatter || {}), [key]: config };
    emit('update:controlMatter', updated);
  } else {
    const updated = { ...(props.statusMatter || {}), [key]: config };
    emit('update:statusMatter', updated);
  }
}

function getBacnetConfigForMapping(valueKey: string, mapping: PropertyMapping): BacnetConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? props.controlBacnet : props.statusBacnet;

  if (source?.[key]) {
    return source[key];
  }

  return source?.[valueKey];
}

function updateBacnetConfigForMapping(valueKey: string, mapping: PropertyMapping, config: BacnetConfig) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);

  if (mapping.referenceType === 'control') {
    const updated = { ...(props.controlBacnet || {}), [key]: config };
    emit('update:controlBacnet', updated);
  } else {
    const updated = { ...(props.statusBacnet || {}), [key]: config };
    emit('update:statusBacnet', updated);
  }
}

function getModbusConfigForMapping(valueKey: string, mapping: PropertyMapping): ModbusConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? props.controlModbus : props.statusModbus;

  if (source?.[key]) {
    return source[key];
  }

  return source?.[valueKey];
}

function updateModbusConfigForMapping(valueKey: string, mapping: PropertyMapping, config: ModbusConfig) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);

  if (mapping.referenceType === 'control') {
    const updated = { ...(props.controlModbus || {}), [key]: config };
    emit('update:controlModbus', updated);
  } else {
    const updated = { ...(props.statusModbus || {}), [key]: config };
    emit('update:statusModbus', updated);
  }
}

function getMqttConfigForMapping(valueKey: string, mapping: PropertyMapping): MQTTConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? props.controlMqtt : props.statusMqtt;

  if (source?.[key]) {
    return source[key];
  }

  return source?.[valueKey];
}

function updateMqttConfigForMapping(valueKey: string, mapping: PropertyMapping, config: MQTTConfig) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);

  if (mapping.referenceType === 'control') {
    const updated = { ...(props.controlMqtt || {}), [key]: config };
    emit('update:controlMqtt', updated);
  } else {
    const updated = { ...(props.statusMqtt || {}), [key]: config };
    emit('update:statusMqtt', updated);
  }
}

function getESPHomeConfigForMapping(valueKey: string, mapping: PropertyMapping): ESPHomeConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? props.controlEsphome : props.statusEsphome;

  if (source?.[key]) {
    return source[key];
  }

  return source?.[valueKey];
}

function updateESPHomeConfigForMapping(valueKey: string, mapping: PropertyMapping, config: ESPHomeConfig | undefined) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source =
    mapping.referenceType === 'control' ? { ...(props.controlEsphome || {}) } : { ...(props.statusEsphome || {}) };

  if (config) {
    source[key] = config;
  } else {
    delete source[key];
  }

  if (mapping.referenceType === 'control') {
    emit('update:controlEsphome', source);
  } else {
    emit('update:statusEsphome', source);
  }
}

function getZWaveConfigForMapping(valueKey: string, mapping: PropertyMapping): ZWaveConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? props.controlZwave : props.statusZwave;

  if (source?.[key]) {
    return source[key];
  }

  return source?.[valueKey];
}

function updateZWaveConfigForMapping(valueKey: string, mapping: PropertyMapping, config: ZWaveConfig | undefined) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source =
    mapping.referenceType === 'control' ? { ...(props.controlZwave || {}) } : { ...(props.statusZwave || {}) };

  if (config) {
    source[key] = config;
  } else {
    delete source[key];
  }

  if (mapping.referenceType === 'control') {
    emit('update:controlZwave', source);
  } else {
    emit('update:statusZwave', source);
  }
}

function getZigbeeConfigForMapping(valueKey: string, mapping: PropertyMapping): ZigbeeConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? props.controlZigbee : props.statusZigbee;

  if (source?.[key]) {
    return source[key];
  }

  return source?.[valueKey];
}

function updateZigbeeConfigForMapping(valueKey: string, mapping: PropertyMapping, config: ZigbeeConfig | undefined) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source =
    mapping.referenceType === 'control' ? { ...(props.controlZigbee || {}) } : { ...(props.statusZigbee || {}) };

  if (config) {
    source[key] = config;
  } else {
    delete source[key];
  }

  if (mapping.referenceType === 'control') {
    emit('update:controlZigbee', source);
  } else {
    emit('update:statusZigbee', source);
  }
}

function getHAConfigForMapping(valueKey: string, mapping: PropertyMapping): HAConfig | undefined {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return undefined;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? props.controlHa : props.statusHa;

  if (source?.[key]) {
    return source[key];
  }

  return source?.[valueKey];
}

function updateHAConfigForMapping(valueKey: string, mapping: PropertyMapping, config: HAConfig | undefined) {
  const allMappings = getAllMappings(valueKey);
  const mappingIndex = allMappings.indexOf(mapping);

  if (mappingIndex === -1) return;

  const key = getMappingKey(valueKey, mapping, mappingIndex);
  const source = mapping.referenceType === 'control' ? { ...(props.controlHa || {}) } : { ...(props.statusHa || {}) };

  if (config) {
    source[key] = config;
  } else {
    delete source[key];
  }

  if (mapping.referenceType === 'control') {
    emit('update:controlHa', source);
  } else {
    emit('update:statusHa', source);
  }
}

// Get color coding for value types
function getValueTypeStyle(valueType: string): string {
  switch (valueType) {
    case 'boolean':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'number':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
    case 'enum':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'string':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'json':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  }
}

// Format current value for display based on valueType
function formatSensorValue(value: unknown, valueType?: string, unit?: string, decimalPlaces?: number): string {
  console.log(
    '[DeviceValuesEditor] formatSensorValue called with:',
    value,
    'type:',
    typeof value,
    'valueType:',
    valueType,
  );

  if (value === null || value === undefined) return '--';

  // Handle nested value object
  if (typeof value === 'object' && 'value' in value) {
    return formatSensorValue((value as { value: unknown }).value, valueType, unit, decimalPlaces);
  }

  // Format based on valueType
  switch (valueType) {
    case 'boolean': {
      const boolVal = typeof value === 'boolean' ? value : Boolean(value);
      return boolVal ? tt('✓ On', '✓ Vklopljeno') : tt('✗ Off', '✗ Izklopljeno');
    }

    case 'number':
      if (typeof value === 'number') {
        const formatted =
          decimalPlaces !== undefined
            ? value.toFixed(decimalPlaces)
            : Number.isInteger(value)
              ? String(value)
              : value.toFixed(2);
        return unit ? `${formatted} ${unit}` : formatted;
      }
      return String(value);

    case 'enum':
      return String(value);

    case 'string':
      return String(value);

    case 'json':
      try {
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
      } catch {
        return String(value);
      }

    default:
      // Fallback to old behavior if no valueType
      if (typeof value === 'number') {
        const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2);
        return unit ? `${formatted} ${unit}` : formatted;
      }
      if (typeof value === 'boolean') return value ? tt('On', 'Vklopljeno') : tt('Off', 'Izklopljeno');
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
  }
}
</script>
